import React, { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ALL_LAYOUTS, LayoutConfig, getLayoutById, getLayoutImage } from '../../constants/layouts';
import { updateConfigurationLayout } from '../../lib/supabase';
import { API_BASE_URL } from '../../config/apiUrl';
import { syncSeniorConfig } from '../../lib/syncConfig';

interface EditConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  seniorId: string;
  seniorName: string;
  currentLayoutId?: string;
  onLayoutUpdated?: (newLayoutId: string) => void;
}

export const EditConfigModal: FC<EditConfigModalProps> = ({
  isOpen,
  onClose,
  seniorId,
  seniorName,
  currentLayoutId,
  onLayoutUpdated,
}) => {
  const [selectedLayoutId, setSelectedLayoutId] = useState<string>(currentLayoutId || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset selection when modal opens with new data
  useEffect(() => {
    if (isOpen && currentLayoutId) {
      setSelectedLayoutId(currentLayoutId);
      setError(null);
    }
  }, [isOpen, currentLayoutId]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleSave = async () => {
    if (!selectedLayoutId || selectedLayoutId === currentLayoutId) {
      onClose();
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // 1. Update layout in Supabase
      await updateConfigurationLayout(seniorId, selectedLayoutId);

      // 2. Trigger real-time sync to Android device via server
      // Auth + ownership now required server-side; syncSeniorConfig attaches
      // the JWT and never throws (see lib/syncConfig.ts).
      await syncSeniorConfig(seniorId);

      onLayoutUpdated?.(selectedLayoutId);
      onClose();
    } catch (err) {
      console.error('Failed to update layout:', err);
      setError('Failed to update layout. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const layoutEntries = Object.values(ALL_LAYOUTS);
  const currentLayout = currentLayoutId ? getLayoutById(currentLayoutId) : null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-brand-text/50 backdrop-blur-sm animate-fadeIn"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          className="bg-white rounded-none sm:rounded-2xl shadow-2xl w-full max-w-4xl max-h-full sm:max-h-[90vh] sm:m-4 m-0 flex flex-col pointer-events-auto animate-slideUp"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-brand-border shrink-0">
            <div>
              <h2 className="font-display text-xl sm:text-2xl font-bold text-brand-text">
                Edit Configuration
              </h2>
              <p className="text-sm text-brand-text-muted mt-1">
                Changing layout for <span className="font-semibold text-brand-text">{seniorName}</span>
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-brand-bg-warm transition-colors"
              aria-label="Close"
            >
              <span className="material-symbols-outlined text-2xl text-brand-text-muted">close</span>
            </button>
          </div>

          {/* Current Layout Info */}
          {currentLayout && (
            <div className="px-4 sm:px-6 py-3 bg-brand-bg-warm border-b border-brand-border shrink-0">
              <p className="text-sm text-brand-text-muted">
                Current layout: <span className="font-semibold text-brand-text">{currentLayout.name}</span>
                <span className="text-brand-text-muted ml-1">({currentLayout.description})</span>
              </p>
            </div>
          )}

          {/* Layout Grid */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {layoutEntries.map((layout) => (
                <LayoutCard
                  key={layout.id}
                  layout={layout}
                  isSelected={selectedLayoutId === layout.id}
                  isCurrent={currentLayoutId === layout.id}
                  onSelect={() => setSelectedLayoutId(layout.id)}
                />
              ))}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="px-4 sm:px-6 py-3 bg-red-50 border-t border-red-100">
              <p className="text-sm text-red-600 flex items-center gap-2">
                <span className="material-symbols-outlined text-lg">error</span>
                {error}
              </p>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-4 sm:p-6 border-t border-brand-border shrink-0 bg-brand-bg-warm/50">
            <button
              onClick={onClose}
              disabled={saving}
              className="px-4 sm:px-6 py-2.5 text-sm font-semibold text-brand-text-light hover:text-brand-text hover:bg-brand-bg-warm rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !selectedLayoutId || selectedLayoutId === currentLayoutId}
              className="flex items-center gap-2 px-4 sm:px-6 py-2.5 text-sm font-semibold text-white bg-brand-primary hover:bg-brand-primary-dark disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
            >
              {saving ? (
                <>
                  <span className="material-symbols-outlined text-lg animate-spin">progress_activity</span>
                  Saving...
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined text-lg">save</span>
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

// Individual layout card
interface LayoutCardProps {
  layout: LayoutConfig;
  isSelected: boolean;
  isCurrent: boolean;
  onSelect: () => void;
}

const LayoutCard: FC<LayoutCardProps> = ({ layout, isSelected, isCurrent, onSelect }) => {
  const { i18n } = useTranslation();
  const [imageError, setImageError] = useState(false);

  return (
    <button
      onClick={onSelect}
      className={`relative flex flex-col rounded-xl border-2 overflow-hidden transition-all hover:shadow-lg active:scale-[0.98] ${
        isSelected
          ? 'border-brand-primary ring-2 ring-brand-primary/20 shadow-md'
          : 'border-brand-border hover:border-brand-primary/50'
      }`}
    >
      {/* Current badge */}
      {isCurrent && (
        <span className="absolute top-2 left-2 z-10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-brand-primary text-white rounded-full">
          Current
        </span>
      )}

      {/* Selected checkmark */}
      {isSelected && !isCurrent && (
        <span className="absolute top-2 right-2 z-10 w-6 h-6 flex items-center justify-center bg-brand-primary text-white rounded-full">
          <span className="material-symbols-outlined text-sm">check</span>
        </span>
      )}

      {/* Layout Image */}
      <div className="aspect-[9/16] bg-brand-bg-warm relative">
        {!imageError ? (
          <img
            src={getLayoutImage(layout, i18n.language)}
            alt={layout.name}
            className="w-full h-full object-contain"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-brand-text-muted">
            <span className="material-symbols-outlined text-4xl mb-2">smartphone</span>
            <span className="text-xs">{layout.id}</span>
          </div>
        )}
      </div>

      {/* Layout Info */}
      <div className="p-3 bg-white text-left">
        <h3 className="font-semibold text-sm text-brand-text line-clamp-1">{layout.name}</h3>
        <p className="text-xs text-brand-text-muted mt-0.5 line-clamp-2">{layout.description}</p>

        {/* Features */}
        <div className="flex flex-wrap gap-1 mt-2">
          {layout.has911 && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-red-50 text-red-600 text-[11px] font-semibold rounded">
              <span className="material-symbols-outlined text-xs">emergency</span>
              911
            </span>
          )}
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-brand-bg-warm text-brand-text-light text-[11px] font-semibold rounded">
            <span className="material-symbols-outlined text-xs">contacts</span>
            {layout.contactCount}
          </span>
          {layout.hasAssistance && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-purple-50 text-purple-600 text-[11px] font-semibold rounded">
              <span className="material-symbols-outlined text-xs">assistant</span>
            </span>
          )}
        </div>
      </div>
    </button>
  );
};

export default EditConfigModal;
